<template>
  <div class="sales-settings h-100">
    <div class="settings-wrapper" >
      <!-- General Settings (Invoice Language & Strict Mode) -->
      <div v-if="activeTab === 'general'" class="sales-settings-content">
        <h6 class="mb-3">{{ $t(moduleName + '_settings') }}</h6>
        <p class="text-muted mb-4 small">{{ $t(moduleName + '_settings_description') }}</p>

        <div class="list-group list-group-flush mb-4">
          <!-- Invoice Language -->
          <div class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div>
              <h6 class="mb-0 fw-bold">{{ $t('invoice_language') }}</h6>
            </div>
            <div style="width: 200px">
              <select v-model="invoiceLanguage" @change="saveInvoiceLanguage" class="form-select bg-white form-select-sm" aria-label="Select invoice language">
                <option value="en">🇺🇸 English</option>
                <option value="fr">🇫🇷 French</option>
                <option value="ar">🇲🇦 Arabic (beta)</option>
              </select>
            </div>
          </div>

          <!-- Invoice Date Format -->
          <div v-if="isSalesModule" class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div>
              <h6 class="mb-0 fw-bold">{{ $t('date_format') }}</h6>
              <p class="text-muted small mb-0">{{ $t('date_format_description') }}</p>
            </div>
            <div style="width: 240px">
              <select v-model="dateFormat" :disabled="saving" @change="saveMetadata('date_format', dateFormat)" class="form-select bg-white form-select-sm" aria-label="Select invoice date format">
                <option v-for="option in dateFormatOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Strict Stock Mode -->
          <div class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div>
              <h6 class="mb-0 fw-bold">{{ $t('strict_stock_mode') }}</h6>
              <p class="text-muted small mb-0">{{ $t('strict_stock_mode_description') }}</p>
            </div>
            <div class="form-check form-switch m-0">
              <input class="form-check-input fs-5 m-0" type="checkbox" id="strictStockMode" v-model="strictStockMode" @change="saveStrictStockMode" style="cursor: pointer;">
            </div>
          </div>

          <!-- Recurring Invoices -->
          <div v-if="isSalesModule" class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div class="pe-3">
              <h6 class="mb-0 fw-bold">{{ $t('recurring_invoices') }}</h6>
              <p class="text-muted small mb-0">{{ $t('recurring_invoices_description') }}</p>
            </div>
            <div class="form-check form-switch m-0">
              <input
                id="recurringInvoicesEnabled"
                v-model="recurringInvoicesEnabled"
                class="form-check-input fs-5 m-0"
                type="checkbox"
                :disabled="saving"
                style="cursor: pointer;"
                @change="saveMetadata('recurring_invoices_enabled', recurringInvoicesEnabled)"
              >
            </div>
          </div>

          <!-- Show Client PO Number -->
          <div class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div>
              <h6 class="mb-0 fw-bold">{{ $t('show_client_po_number') }}</h6>
            </div>
            <div class="form-check form-switch m-0">
              <input class="form-check-input fs-5 m-0" type="checkbox" id="showClientPoNumber" v-model="showClientPoNumber" @change="saveMetadata('show_client_po_number', showClientPoNumber)" style="cursor: pointer;">
            </div>
          </div>

          <!-- Show Client IN Number -->
          <div class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div>
              <h6 class="mb-0 fw-bold">{{ $t('show_client_in_number') }}</h6>
            </div>
            <div class="form-check form-switch m-0">
              <input class="form-check-input fs-5 m-0" type="checkbox" id="showClientInNumber" v-model="showClientInNumber" @change="saveMetadata('show_client_in_number', showClientInNumber)" style="cursor: pointer;">
            </div>
          </div>

          <!-- Show Client DN Number -->
          <div class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div>
              <h6 class="mb-0 fw-bold">{{ $t('show_client_dn_number') }}</h6>
            </div>
            <div class="form-check form-switch m-0">
              <input class="form-check-input fs-5 m-0" type="checkbox" id="showClientDnNumber" v-model="showClientDnNumber" @change="saveMetadata('show_client_dn_number', showClientDnNumber)" style="cursor: pointer;">
            </div>
          </div>

          <!-- Show editable product unit in quote and invoice forms -->
          <div v-if="isSalesModule" class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div>
              <h6 class="mb-0 fw-bold">{{ $t('show_product_unit') }}</h6>
              <p class="text-muted small mb-0">{{ $t('show_product_unit_description') }}</p>
            </div>
            <div class="form-check form-switch m-0">
              <input
                id="showProductUnit"
                v-model="showProductUnit"
                class="form-check-input fs-5 m-0"
                type="checkbox"
                :disabled="saving"
                style="cursor: pointer;"
                @change="saveMetadata('show_product_unit', showProductUnit)"
              >
            </div>
          </div>

          <!-- Allow custom document lines to be copied to the product catalog -->
          <div v-if="isSalesModule" class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div class="pe-3">
              <h6 class="mb-0 fw-bold">{{ $t('allow_custom_product_catalog_creation') }}</h6>
              <p class="text-muted small mb-0">{{ $t('allow_custom_product_catalog_creation_description') }}</p>
            </div>
            <div class="form-check form-switch m-0">
              <input
                id="allowCustomProductCatalogCreation"
                v-model="allowCustomProductCatalogCreation"
                class="form-check-input fs-5 m-0"
                type="checkbox"
                :disabled="saving"
                style="cursor: pointer;"
                @change="saveMetadata('allow_custom_product_catalog_creation', allowCustomProductCatalogCreation)"
              >
            </div>
          </div>

          <!-- Previous prices for the selected client and product -->
          <div v-if="isSalesModule" class="list-group-item px-0 py-3 d-flex justify-content-between align-items-center bg-transparent border-bottom">
            <div class="pe-3">
              <h6 class="mb-0 fw-bold">{{ $t('show_client_product_sales_history') }}</h6>
              <p class="text-muted small mb-0">{{ $t('show_client_product_sales_history_description') }}</p>
            </div>
            <div class="form-check form-switch m-0">
              <input
                id="showClientProductSalesHistory"
                v-model="showClientProductSalesHistory"
                class="form-check-input fs-5 m-0"
                type="checkbox"
                :disabled="saving"
                style="cursor: pointer;"
                @change="saveMetadata('show_client_product_sales_history', showClientProductSalesHistory)"
              >
            </div>
          </div>

          <!-- Receipt Name -->
          <div v-if="isSalesModule && canEditReceiptName" class="list-group-item px-0 py-3 d-flex flex-wrap justify-content-between align-items-center gap-3 bg-transparent border-bottom">
            <div>
              <h6 class="mb-0 fw-bold">{{ $t('process_document_name') }}</h6>
              <p class="text-muted small mb-0">{{ $t('process_name_example') }}</p>
            </div>
            <div class="input-group input-group-sm" style="width: min(100%, 360px);">
              <input
                v-model.trim="receiptName"
                maxlength="100"
                class="form-control bg-white"
                :placeholder="$t('default_process_name')"
                @keyup.enter="saveReceiptName"
              >
              <button class="btn btn-success" type="button" :disabled="saving || !receiptName" @click="saveReceiptName">
                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('save') }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="isSalesModule" class="card border mb-4">
          <div class="card-body">
            <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
              <div>
                <h6 class="mb-1 fw-bold">
                  <AppIcon name="bi-table" class="bi bi-table me-2" />{{ $t('sales_document_columns') }}
                </h6>
                <p class="text-muted small mb-0">{{ $t('sales_document_columns_description') }}</p>
              </div>
              <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="saving" @click="resetDocumentColumns">
                <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise me-1" />{{ $t('reset') }}
              </button>
            </div>

            <div class="btn-group mb-4" role="group" :aria-label="$t('sales_document_columns')">
              <button
                v-for="documentType in documentColumnTypes"
                :key="documentType"
                type="button"
                class="btn btn-sm"
                :class="activeDocumentColumnType === documentType ? 'btn-dark' : 'btn-outline-secondary'"
                @click="activeDocumentColumnType = documentType"
              >
                {{ $t(documentType) }}
              </button>
            </div>

            <div class="row g-3">
              <div v-for="(isVisible, key) in activeDocumentColumns" :key="key" class="col-md-4 col-sm-6">
                <div class="form-check form-switch">
                  <input
                    :id="`${activeDocumentColumnType}-column-${key}`"
                    v-model="documentColumns[activeDocumentColumnType][key]"
                    class="form-check-input"
                    type="checkbox"
                    :disabled="saving"
                    @change="saveDocumentColumns"
                  >
                  <label class="form-check-label" :for="`${activeDocumentColumnType}-column-${key}`">
                    {{ $t(getDocumentColumnLabel(key)) }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <suggestion-settings v-if="activeTab === 'suggestions' && isSalesModule" class="sales-settings-content" />
      
      <!-- Reference Builder Settings -->
      <div v-if="activeTab === 'reference_builder'" class="sales-settings-content">
        <h6 class="mb-3">{{ $t('reference_builder') }}</h6>
        <p class="text-muted mb-4 small">{{ $t('reference_builder_description') }}</p>

        <div class="row">
          <!-- Left Column: Inputs -->
          <div class="col-md-7">
            <div class="list-group list-group-flush mb-4 pe-md-4">
              <div v-for="type in referenceTypes" :key="type" class="list-group-item px-0 py-3 bg-transparent border-bottom">
                <div class="mb-2">
                  <h6 class="mb-1 fw-bold">{{ $t(type + '_reference_format') }}</h6>
                  <p class="text-muted extra-small mb-2">{{ $t('default') }}: <code>{{ getDefaultFormat(type) }}</code></p>
                </div>
                <div class="input-group input-group-sm">
                  <input 
                    type="text" 
                    v-model="referenceFormats[type]" 
                    class="form-control bg-white" 
                    :placeholder="getDefaultFormat(type)"
                  >
                  <button class="btn btn-outline-secondary" type="button" @click="referenceFormats[type] = ''">
                    <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise" />
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Manual Save Button -->
            <div class="d-flex justify-content-end mt-2 pe-md-4 mb-4">
              <button 
                class="btn btn-success d-flex align-items-center shadow-sm px-4 fw-bold" 
                @click="saveReferenceFormats" 
                :disabled="saving"
                style="border-radius: 8px;"
              >
                <div v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></div>
                <AppIcon name="bi-check2-circle" v-else class="bi bi-check2-circle me-2 fs-5" />
                {{ $t('save') }}
              </button>
            </div>
          </div>

          <!-- Right Column: Documentation & Preview -->
          <div class="col-md-5">
            <div class="card border-0 shadow-sm bg-light mb-4">
              <div class="card-body p-3">
                <h6 class="fw-bold mb-3 small text-uppercase" style="letter-spacing: 0.5px;">{{ $t('reference_placeholders_info') }}</h6>
                <div class="d-flex flex-wrap gap-2 mb-4">
                  <span class="badge bg-white text-dark border p-2 font-monospace">{ID}</span>
                  <span class="badge bg-white text-dark border p-2 font-monospace">{DAY}</span>
                  <span class="badge bg-white text-dark border p-2 font-monospace">{MONTH}</span>
                  <span class="badge bg-white text-dark border p-2 font-monospace">{YEAR}</span>
                  <span class="badge bg-white text-dark border p-2 font-monospace">START(192,{ID})</span>
                </div>
                <p class="text-muted extra-small mb-4">{{ $t('reference_start_help') }}</p>

                <h6 class="fw-bold mb-3 small text-uppercase" style="letter-spacing: 0.5px;">{{ $t('live_preview') }}</h6>
                <div v-for="type in referenceTypes" :key="'prev-'+type" class="mb-3 last-child-mb-0">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="extra-small text-muted">{{ $t(type) }}</span>
                  </div>
                  <div class="p-2 bg-white rounded border border-dashed text-primary fw-bold font-monospace small">
                    {{ getPreview(type) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="alert alert-info py-2 px-3 extra-small border-0 shadow-sm">
                <AppIcon name="bi-info-circle-fill" class="bi bi-info-circle-fill me-1" />
                {{ $t('changes_take_effect_after_save') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Invoice Template Builder -->
      <div v-if="activeTab === 'template'" class="flex-grow-1 d-flex flex-column overflow-hidden">
        <invoice-template-picker @templateSaved="$emit('templateSaved')" />
      </div>
    </div>
  </div>
</template>

<script>
import InvoiceTemplatePicker from '../../components/InvoiceTemplatePicker.vue';
import SuggestionSettings from './SuggestionSettings.vue';
import helper from '../../helper';
import {
  SALES_DOCUMENT_COLUMN_LABELS,
  getSalesDocumentColumnDefaults,
  getSalesDocumentColumnMetadataKey
} from '../../constants/salesDocumentColumns';

export default {
  name: 'SalesSettings',
  components: {
    InvoiceTemplatePicker,
    SuggestionSettings
  },
  props: {
    activeTab: String,
    moduleName: {
      type: String,
      default: 'sales'
    }
  },
  data() {
    return {
      showClientDnNumber: false,
      showProductUnit: false,
      allowCustomProductCatalogCreation: false,
      showClientProductSalesHistory: false,
      recurringInvoicesEnabled: false,
      receiptName: '',
      saving: false,
      activeDocumentColumnType: 'invoice',
      documentColumns: {
        invoice: getSalesDocumentColumnDefaults('invoice'),
        quote: getSalesDocumentColumnDefaults('quote'),
        delivery: getSalesDocumentColumnDefaults('delivery')
      },
      dateFormat: 'YYYY-MM-DD',
      dateFormatOptions: [
        { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-08-27)' },
        { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD (2026/08/27)' },
        { value: 'YYYY/DD/MM', label: 'YYYY/DD/MM (2026/27/08)' },
        { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (27-08-2026)' },
        { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (27/08/2026)' },
        { value: 'DD/YYYY/MM', label: 'DD/YYYY/MM (27/2026/08)' },
        { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY (08-27-2026)' },
        { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (08/27/2026)' },
        { value: 'MM/YYYY/DD', label: 'MM/YYYY/DD (08/2026/27)' }
      ],
      referenceFormats: {
        invoice: '',
        quote: '',
        order: '',
        delivery: '',
        supplier_invoice: '',
        purchase: '',
        reception: '',
        return: '',
        supplier_return: ''
      }
    };
  },
  computed: {
    isSalesModule() {
      return this.moduleName === 'sales'
    },
    canEditReceiptName() {
      return ['owner', 'admin'].includes(this.$store.getters.ROLE)
    },
    referenceTypes() {
      if (this.moduleName === 'purchase' || this.moduleName === 'purchases') {
        return ['supplier_invoice', 'purchase', 'reception']
      }
      return ['invoice', 'quote', 'order', 'delivery']
    },
    documentColumnTypes() {
      return ['invoice', 'quote', 'delivery']
    },
    activeDocumentColumns() {
      return this.documentColumns[this.activeDocumentColumnType]
    },
    business() {
      return this.$store.getters.BUSINESS;
    }
  },
  methods: {
    getDocumentColumnLabel(key) {
      return SALES_DOCUMENT_COLUMN_LABELS[key] || key
    },
    loadDocumentColumns() {
      this.documentColumnTypes.forEach(documentType => {
        const metadataKey = getSalesDocumentColumnMetadataKey(documentType)
        this.documentColumns[documentType] = {
          ...getSalesDocumentColumnDefaults(documentType),
          ...(this.business?.metadata?.[metadataKey] || {})
        }
      })
    },
    async saveDocumentColumns() {
      const documentType = this.activeDocumentColumnType
      const metadataKey = getSalesDocumentColumnMetadataKey(documentType)
      await this.saveMetadata(metadataKey, { ...this.documentColumns[documentType] })
    },
    async resetDocumentColumns() {
      this.documentColumns[this.activeDocumentColumnType] = getSalesDocumentColumnDefaults(this.activeDocumentColumnType)
      await this.saveDocumentColumns()
    },
    async saveReceiptName() {
      const receiptName = this.receiptName.trim()
      if (!receiptName) return
      await this.saveMetadata('process_document_name', receiptName)
    },
    async saveStrictStockMode() {
      try {
        this.saving = true;
        if (!this.business.metadata) this.business.metadata = {};
        this.business.metadata.strict_stock_mode = this.strictStockMode;
        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata: this.business.metadata },
          { showToast: true }
        );
        this.$store.commit('SET_BUSINESS', this.business);
      } catch (error) {
        this.$toast.error('Something went wrong');
      } finally {
        this.saving = false;
      }
    },
    async saveInvoiceLanguage() {
      try {
        this.saving = true;
        if (!this.business.metadata) this.business.metadata = {};
        this.business.metadata.invoice_language = this.invoiceLanguage;
        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata: this.business.metadata },
          { showToast: true }
        );
        this.$store.commit('SET_BUSINESS', this.business);
      } catch (error) {
        this.$toast.error('Something went wrong');
      } finally {
        this.saving = false;
      }
    },
    async saveMetadata(key, value) {
      try {
        this.saving = true;
        if (!this.business.metadata) this.business.metadata = {};
        this.business.metadata[key] = value;
        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata: this.business.metadata },
          { showToast: true }
        );
        this.$store.commit('SET_BUSINESS', this.business);
      } catch (error) {
        this.$toast.error('Something went wrong');
      } finally {
        this.saving = false;
      }
    },
    async saveReferenceFormats() {
      try {
        this.saving = true;
        if (!this.business.metadata) this.business.metadata = {};
        this.business.metadata.reference_formats = this.referenceFormats;
        await helper.customPost(
          `${process.env.VUE_APP_API}/businesses`,
          { metadata: this.business.metadata },
          { showToast: true }
        );
        this.$store.commit('SET_BUSINESS', this.business);
      } catch (error) {
        this.$toast.error('Something went wrong');
      } finally {
        this.saving = false;
      }
    },
    getDefaultFormat(type) {
        const defaults = {
            invoice: 'IN-00{ID}-{DAY}-{MONTH}-{YEAR}',
            quote: 'Q-00{ID}-{DAY}-{MONTH}-{YEAR}',
            order: 'ORD-00{ID}-{DAY}-{MONTH}-{YEAR}',
            delivery: 'DEL-00{ID}-{DAY}-{MONTH}-{YEAR}',
            supplier_invoice: 'SI-00{ID}-{DAY}-{MONTH}-{YEAR}',
            purchase: 'PUR-00{ID}-{DAY}-{MONTH}-{YEAR}',
            reception: 'REC-00{ID}-{DAY}-{MONTH}-{YEAR}',
            return: 'RET-00{ID}-{DAY}-{MONTH}-{YEAR}',
            supplier_return: 'SR-00{ID}-{DAY}-{MONTH}-{YEAR}'
        };
        return defaults[type] || '{ID}';
    },
    getPreview(type) {
        const format = this.referenceFormats[type] || this.getDefaultFormat(type);
        const now = new Date();
        return format
            .replace(/START\(\s*(\d+)\s*,\s*\{ID\}\s*\)/gi, (_match, start) => String(Number(start) + 1))
            .replace(/{ID}/g, '1')
            .replace(/{DAY}/g, String(now.getDate()).padStart(2, '0'))
            .replace(/{MONTH}/g, String(now.getMonth() + 1).padStart(2, '0'))
            .replace(/{YEAR}/g, String(now.getFullYear()));
    }
  },
  mounted() {
    this.invoiceLanguage  = this.business?.metadata?.invoice_language || this.business?.language || 'en';
    const savedDateFormat = this.business?.metadata?.date_format;
    this.dateFormat = this.dateFormatOptions.some(option => option.value === savedDateFormat)
      ? savedDateFormat
      : 'YYYY-MM-DD';
    this.strictStockMode  = this.business?.metadata?.strict_stock_mode || false;
    this.showClientPoNumber = this.business?.metadata?.show_client_po_number || false;
    this.showClientInNumber = this.business?.metadata?.show_client_in_number || false;
    this.showClientDnNumber = this.business?.metadata?.show_client_dn_number || false;
    this.showProductUnit = this.business?.metadata?.show_product_unit === true;
    this.allowCustomProductCatalogCreation = this.business?.metadata?.allow_custom_product_catalog_creation === true;
    this.showClientProductSalesHistory = this.business?.metadata?.show_client_product_sales_history === true;
    this.recurringInvoicesEnabled = this.business?.metadata?.recurring_invoices_enabled === true;
    this.receiptName = this.business?.metadata?.process_document_name || this.$t('default_process_name');
    this.loadDocumentColumns();
    
    if (this.business?.metadata?.reference_formats) {
      this.referenceFormats = { 
        ...this.referenceFormats, 
        ...this.business.metadata.reference_formats 
      };
    }
  }
};
</script>

<style scoped>
.sales-settings {
  padding: 0;
}

.settings-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sales-settings-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
}

.extra-small {
  font-size: 0.7rem;
}

.last-child-mb-0:last-child {
  margin-bottom: 0 !important;
}

.border-dashed {
  border-style: dashed !important;
}
</style>
